import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMissingKeys, clearMissingKeys } from '@/i18n/config';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, AlertTriangle, Globe } from 'lucide-react';

export const I18nDebugBanner = () => {
  const { i18n } = useTranslation();
  const [missingKeys, setMissingKeys] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Check for missing keys periodically
    const interval = setInterval(() => {
      setMissingKeys(getMissingKeys());
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Only show in development
  if (!import.meta.env.DEV || !isVisible) {
    return null;
  }

  const hasMissingKeys = missingKeys.length > 0;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-sm">
      <div className={`rounded-lg border shadow-lg p-3 text-xs ${
        hasMissingKeys 
          ? 'bg-destructive/10 border-destructive/30' 
          : 'bg-muted/90 border-border'
      }`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">i18n Debug</span>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {i18n.language}
            </Badge>
            {hasMissingKeys && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                {missingKeys.length} missing
              </Badge>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-5 w-5 p-0"
            onClick={() => setIsVisible(false)}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>

        {hasMissingKeys && (
          <div className="mt-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-destructive hover:underline"
            >
              <AlertTriangle className="w-3 h-3" />
              <span>{isExpanded ? 'Hide' : 'Show'} missing keys</span>
            </button>
            
            {isExpanded && (
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {missingKeys.slice(0, 20).map((key) => (
                  <div 
                    key={key} 
                    className="font-mono text-[10px] text-destructive bg-destructive/10 px-1.5 py-0.5 rounded truncate"
                    title={key}
                  >
                    {key}
                  </div>
                ))}
                {missingKeys.length > 20 && (
                  <div className="text-muted-foreground">
                    ...and {missingKeys.length - 20} more
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px] mt-1"
                  onClick={() => {
                    clearMissingKeys();
                    setMissingKeys([]);
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default I18nDebugBanner;
